import * as Sentry from '@sentry/browser';

import { ErrorCode, Log } from '@biteinc/common';
import type { Order, Transaction } from '@biteinc/core-react';
import { StringHelper, TimeHelper } from '@biteinc/core-react';
import {
  FlashBridgeMessage,
  OrderClientEventName,
  OrderSkipReason,
  PaymentsApiVersion,
  TransactionResult,
  TransactionUserAction,
} from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import type { GcnOrderedItem } from '~/types/gcn_ordered_item';
import { OrderFlowState } from '~/types/order_flow_state';

import { GcnPaymentHelper } from '../../helpers';
import type { GcnOrder } from '../../types/gcn_order';
import type { GcnError } from './gcn_bridge_interface';
import GcnHardwareManager from './gcn_hardware_manager';
import type { CloseOrderResponse, GetOrderResponse, KdsJob } from './gcn_maitred_client';
import OrderHelper from './gcn_order_helper';
import { GCNRouterHelper } from './gcn_router_helper';
import { I9nManager } from './i9ns/i9n_manager';
import { localizeStr } from './localization/localization';
import { GCNTransaction } from './models/gcn_transaction';
import {
  ensureOrderIsInSyncWithServer,
  handleFailedToGetOrder,
  printGuestReceipt,
  setOrderFlowState,
  shouldAbortSending,
} from './order_sender.utils';
import Analytics from './utils/analytics';
import { sleep } from './utils/promises';
import { GCNAlertView } from './views/gcn_alert_view';

module OrderSender {
  async function performDemoPayment(
    currentOrder: GcnOrder,
    chargeTotal: number,
  ): Promise<{ transaction: Transaction; queuePath?: string }> {
    const transaction = GCNTransaction.demoPayTransactionWithAmount(
      chargeTotal,
      currentOrder.get('clientId'),
      PaymentsApiVersion.V2,
      gcn.location.get('orgId'),
      gcn.location.id,
      gcn.kiosk?.id, // Will be undefined for kiosk preview
    );

    const queuePath = await captureTransactionOnMaitred(currentOrder, transaction);

    return { transaction, queuePath };
  }

  async function performPaymentFromKiosk(
    currentOrder: GcnOrder,
    chargeTotal: number,
  ): Promise<{ transaction: Transaction; queuePath?: string }> {
    const transaction = await gcn.bridge.sendAsync<any>({
      event: FlashBridgeMessage.MAKE_PAYMENT,
      amountInCents: chargeTotal,
      order: currentOrder.attributes,
    });

    const queuePath = await captureTransactionOnMaitred(currentOrder, transaction);

    return { transaction, queuePath };
  }

  async function performPaymentFromMaitred(): Promise<{
    transaction: Transaction;
    queuePath?: string;
  }> {
    const result = await gcn.maitred.createKioskApiPaymentRequest({ ebtMode: null });
    return { transaction: result.transaction, queuePath: result.queuePath };
  }

  export async function cancelPaymentFromMaitred(): Promise<boolean> {
    const result = await gcn.maitred.cancelKioskApiPaymentRequest();
    return result.success;
  }

  async function captureTransactionOnMaitred(
    currentOrder: GcnOrder,
    transaction: Transaction,
  ): Promise<string | undefined> {
    const response = await gcn.maitred.createKioskPaymentRequest({
      orderId: currentOrder.id,
      transaction,
    });
    // @todo - remove this check once all api driven payment terminal integrations are stable
    // if (!window.isGarcon) {
    gcn.bridge.send({
      event: FlashBridgeMessage.SENT_PAYMENT_TO_MAITRED,
      clientId: transaction.clientId,
    });

    return response.queuePath;
    // }
  }

  async function closeFlashOrderWithRetries(
    orderId: string,
    orderPayload: Record<string, any>,
    timeout: number,
    startedAt: number,
  ): Promise<any> {
    try {
      const closeOrderResponse = await gcn.maitred.closeOrderRequest({
        timeout,
        startedAt,
        code: ErrorCode.SendOrderUITimeoutWithPayment,
        message: 'Send order UI time out',
      });
      return closeOrderResponse;
    } catch (err) {
      // order already closed if our close request timed out
      const orderAlreadyClosed =
        gcn.maitred.isMaitredApiError(err) && err.code === ErrorCode.OrderClosed;
      if (orderAlreadyClosed) {
        try {
          const getOrderResponse = await gcn.maitred.getOrderRequest(orderId);
          return getOrderResponse;
        } catch {
          // best effort
        }
        // fall through and rely on idempotency
      }

      if (shouldAbortSending(err.code)) {
        throw err;
      }

      if (Date.now() - startedAt >= timeout) {
        throw {
          code: ErrorCode.SendOrderUITimeoutWithPayment,
          message: 'Send order UI time out',
        };
      }
    }

    await sleep(1000);
    return closeFlashOrderWithRetries(orderId, orderPayload, timeout, startedAt);
  }

  export async function sendFlashOrder(
    currentOrder: GcnOrder,
    orderPayload: Record<string, any>,
    orderUpdatePayload: Record<string, any>,
    hasEcommPayment: boolean,
    skipSettingOrderFlowStateSending?: true,
  ): Promise<void> {
    const orderId = currentOrder.id;
    if (!orderPayload.order.clientId) {
      orderPayload.order.clientId = StringHelper.newMongoId();
    }

    // In the case that this is a future order, we may have already closed the order
    if (currentOrder.isClosed() && currentOrder.isBiteFutureOrder()) {
      setOrderFlowState(OrderFlowState.OrderComplete, currentOrder.toJSON());
      // Update hash for GA tracking
      GCNRouterHelper.navToOrderConfirmation();
      return;
    }

    // Skip this so we don't hide the spinner
    if (!skipSettingOrderFlowStateSending) {
      setOrderFlowState(OrderFlowState.SendingOrder);
    }

    // Always surface the errors from these calls to the user
    try {
      await gcn.maitred.updateOrderRequest(orderUpdatePayload);

      // Create flash payment
      if (hasEcommPayment) {
        if (skipSettingOrderFlowStateSending) {
          gcn.orderManager.eventRepo.track(OrderClientEventName.PaymentCreditCardStart);
          Analytics.track(Analytics.EventName.CreditCardPaymentStart);
        }

        await gcn.maitred.createEcommPaymentRequest();

        if (skipSettingOrderFlowStateSending) {
          gcn.orderManager.eventRepo.track(OrderClientEventName.PaymentCreditCardEnd);
          Analytics.track(Analytics.EventName.CreditCardPaymentComplete);
        }
      }
    } catch (err) {
      setOrderFlowState(OrderFlowState.FlashOrderFailed, undefined, err);
      Sentry.captureMessage(err.message, {
        extra: {
          order: orderPayload.order,
          error: err,
        },
      });
      return;
    }

    let response: { order: Order; successfulTransactions: Transaction[] };
    try {
      if (skipSettingOrderFlowStateSending) {
        // Since we aren't setting the order flow state to sending-order, we have to track the event
        gcn.orderManager.eventRepo.track(OrderClientEventName.OrderSendStart);
        Analytics.track(Analytics.EventName.OrderSendStart);
      }

      const orderSubmitTimeout = gcn.location.get('orderSubmitTimeout');

      response = await closeFlashOrderWithRetries(
        orderId,
        orderPayload,
        gcn.orderManager.getOrder()!.isSentImmediately()
          ? orderSubmitTimeout
          : TimeHelper.MINUTE * 2,
        Date.now(),
      );

      const orderJson = {
        ...response.order,
        transactions: response.successfulTransactions,
      };
      setOrderFlowState(OrderFlowState.SendingOrderSuccess, orderJson);
      setOrderFlowState(OrderFlowState.OrderComplete, orderJson);
      // Update hash for GA tracking
      GCNRouterHelper.navToOrderConfirmation();
    } catch (err) {
      Sentry.captureMessage(err.message, {
        extra: {
          order: orderPayload.order,
          error: err,
        },
      });
      setOrderFlowState(OrderFlowState.SendingOrderFailed, undefined, err);
    }
  }

  async function takeKioskPayment(
    currentOrder: GcnOrder,
    chargeTotal: number,
  ): Promise<{ accepted: boolean; transaction?: Transaction; queuePath?: string }> {
    const skippingPayment =
      !gcn.location.takesPayment() || gcn.orderManager.payingAtCashier() || chargeTotal === 0;

    if (skippingPayment) {
      return { accepted: true, transaction: undefined };
    }

    if (!window.isGarcon) {
      setOrderFlowState(OrderFlowState.WaitingForCard);
    }

    let transaction: Transaction;
    let queuePath: string | undefined;
    try {
      if (
        (window.isGarcon || gcn.location.usesApiForTerminalPayments()) &&
        !window.hasPaymentTerminal
      ) {
        const result = await performDemoPayment(currentOrder, chargeTotal);
        transaction = result.transaction;
        queuePath = result.queuePath;
        /**
         * @todo
         * Revert to checking schema once all api driven payment terminal integrations are stable
         */
      } else if (gcn.location.usesApiForTerminalPayments()) {
        const result = await performPaymentFromMaitred();
        transaction = result.transaction;
        queuePath = result.queuePath;
      } else {
        const result = await performPaymentFromKiosk(currentOrder, chargeTotal);
        transaction = result.transaction;
        queuePath = result.queuePath;
      }
    } catch (err) {
      Sentry.captureMessage(err.message, {
        extra: {
          order: currentOrder,
          error: err,
        },
      });
      setOrderFlowState(OrderFlowState.ProcessingPaymentFailed, undefined, err);
      return { accepted: false, transaction: undefined, queuePath };
    }

    const cancelled = transaction.userAction === TransactionUserAction.Cancelled;
    const abandoned = transaction.userAction === TransactionUserAction.Abandoned;
    const approved = transaction.result === TransactionResult.Approved;

    if (cancelled) {
      setOrderFlowState(OrderFlowState.TransactionCancelled);
      return { accepted: false, queuePath };
    }

    if (abandoned) {
      setOrderFlowState(OrderFlowState.OrderAbandoned);
      return { accepted: false, queuePath };
    }

    if (!approved) {
      const shouldRetry = await new Promise((resolve) => {
        const canWeRetry = GcnPaymentHelper.canWeRetryKioskPayment(transaction.errorCode);

        const message = GcnPaymentHelper.getKioskPaymentErrorMessage(
          transaction.identifiedErrorCode,
          transaction.entryMode,
          transaction.cardSchemeId,
        )
          .split('\n')
          .join('<br />');

        let timeout: NodeJS.Timeout;

        const confirmView = new GCNAlertView({
          text: message,
          okText: localizeStr(Strings.OK),
          okCallback: () => {
            gcn.menuView.dismissModalPopup();
            clearTimeout(timeout);
            if (canWeRetry) {
              resolve(true);
            } else {
              setOrderFlowState(OrderFlowState.TransactionCancelled);
              resolve(false);
            }
          },
        });
        gcn.menuView.showModalPopup(confirmView);

        // We want a timeout without a cancel button, so we'll set this outside of the confirm view
        timeout = setTimeout(() => {
          gcn.menuView.dismissModalPopup();
          confirmView.destroy();
          resolve(false);
        }, 15000);
      });

      if (shouldRetry) {
        return takeKioskPayment(currentOrder, chargeTotal);
      }

      setOrderFlowState(OrderFlowState.TransactionCancelled);
      return { accepted: false, transaction, queuePath };
    }

    return { accepted: true, transaction, queuePath };
  }

  async function sendKdsJobSocketRequest(
    job: KdsJob,
    orderId: string,
    startedAt: number,
  ): Promise<void> {
    try {
      const address = `${job.kds.ipAddress}:${job.kds.port}`;
      const kdsResult = await gcn.bridge.sendAsync<{ success: boolean; response: string }>({
        event: FlashBridgeMessage.SOCKET_REQUEST,
        address,
        payload: job.payload,
      });
      Log.debug('KDS result', kdsResult);
      /**
       * We are only going to get an error here if we fail to connect to the KDS. The backend is
       * responsible for handling any errors that come from the KDS.
       * @todo - handle errors from the KDS in Tournant for full on-prem mode
       */
      if (!kdsResult.success) {
        throw Error('Failed to connect to KDS');
      }
      const sendAttemptResult = await gcn.maitred.postFulfillmentSendAttempt(orderId, {
        startedAt,
        response: {
          data: kdsResult.response,
          address,
        },
      });
      Log.debug('KDS send attempt result', sendAttemptResult);
    } catch (err) {
      Log.debug('KDS error', err);
    }
  }

  async function sendKdsJob(orderId: string): Promise<void> {
    if (!gcn.location.useOnPremKds()) {
      return;
    }
    const startedAt = Date.now();
    const result = await gcn.maitred.getKdsOrderPayload(orderId);
    Log.debug('KDS payload', result);
    /**
     * FRESH-KDS
     * - Items can be routed to specific devices based on stations
     * - the backend will generate all the necessary jobs for the devices
     *
     * LOGIC-CONTROLS
     * - Items are sent to a single device which acts as the primary router and controls
     * all routing logic
     */

    // Promise.all waits for all the kdsJobs to finish before continuing
    // fixes the problem of orders not closing
    await Promise.all(
      result.kdsJobs.map((job) => {
        return sendKdsJobSocketRequest(job, orderId, startedAt);
      }),
    );
  }

  async function sendKioskOrder(currentOrder: GcnOrder): Promise<{ order: GcnOrder }> {
    setOrderFlowState(OrderFlowState.SendingOrder);

    try {
      // Fulfillment methods
      await sendKdsJob(currentOrder.id);

      let closeOrderResponse: CloseOrderResponse;
      if (currentOrder.isSentImmediately()) {
        closeOrderResponse = await gcn.maitred.closeAsapKioskOrder();

        gcn.orderManager.setOrderFromJSON({
          ...closeOrderResponse.order,
          transactions: closeOrderResponse.successfulTransactions,
        });
      } else {
        closeOrderResponse = await gcn.maitred.closeOrderRequest({
          timeout: TimeHelper.SECOND * 45,
          startedAt: Date.now(),
          code: ErrorCode.NetworkRequestTimedOut,
          message: 'Order close request timed out',
        });
      }

      const order = {
        ...closeOrderResponse.order,
        transactions: closeOrderResponse.successfulTransactions,
      };

      setOrderFlowState(OrderFlowState.SendingOrderSuccess, order);

      const orderedItems: GcnOrderedItem[] = [];
      return { order: OrderHelper.orderFromJson(order, orderedItems) };
    } catch (err) {
      setOrderFlowState(OrderFlowState.SendingOrderFailed, undefined, err);
      throw err;
    }
  }

  export async function startOrderFlow(
    orderPayload: Record<string, any>,
    orderUpdatePayload: Record<string, any>,
    currentOrder: GcnOrder,
    hasEcommPayment: boolean,
    checkoutSession: CheckoutSession,
    chargeTotal: number,
  ): Promise<void> {
    let serverOrder: GetOrderResponse;
    try {
      serverOrder = await gcn.maitred.getOrderRequest(currentOrder.id);
    } catch {
      handleFailedToGetOrder(currentOrder);
      return;
    }

    if (!(await ensureOrderIsInSyncWithServer(currentOrder, serverOrder))) {
      return;
    }

    if (window.offlineDemoMode) {
      checkoutSession.orderPayload.wasSentToPos = Date.now();
      checkoutSession.orderPayload.wasSentForFulfillment = true;
      checkoutSession.orderPayload.sendingToPosWasSkipped = OrderSkipReason.OfflineDemoMode;
    }

    if (window.isFlash) {
      await sendFlashOrder(currentOrder, orderPayload, orderUpdatePayload, hasEcommPayment);
      return;
    }

    try {
      await gcn.maitred.updateOrderRequest(orderUpdatePayload);
    } catch (err) {
      Sentry.captureException(err);

      setOrderFlowState(OrderFlowState.SendingOrderFailed, undefined, err);
      return;
    }

    const { accepted: successfulPayment, queuePath } = await takeKioskPayment(
      currentOrder,
      chargeTotal,
    );

    if (!successfulPayment) {
      return;
    }

    await finalizeCloseOrder(
      queuePath
        ? async () => await awaitCloseOrder(queuePath, successfulPayment)
        : async () => await sendKioskOrder(currentOrder),
      checkoutSession,
      currentOrder,
    );
  }

  export async function awaitCloseOrder(
    queuePath: string,
    successfulPayment: boolean,
    orderUpdatePayload?: Record<string, any>,
  ): Promise<{ order: GcnOrder; closeSuccessful: true; successfulTransactions: Transaction[] }> {
    setOrderFlowState(OrderFlowState.SendingOrder);
    gcn.menuView.showSpinner(localizeStr(Strings.SENDING_ORDER_KITCHEN));

    try {
      if (orderUpdatePayload) {
        await gcn.maitred.updateOrderRequest(orderUpdatePayload);
      }

      const { order, successfulTransactions } = await gcn.maitred.awaitCloseOrderResponse(
        queuePath,
        successfulPayment,
      );

      const orderWithTransactions = {
        ...order,
        transactions: successfulTransactions,
      };
      gcn.orderManager.setOrderFromJSON(orderWithTransactions);

      try {
        await sendKdsJob(order._id);
      } catch (err) {
        Sentry.captureException(err);
      }

      setOrderFlowState(OrderFlowState.SendingOrderSuccess, orderWithTransactions);

      const updatedOrder = OrderHelper.orderFromJson(orderWithTransactions, []);

      return { order: updatedOrder, successfulTransactions, closeSuccessful: true };
    } catch (err) {
      Sentry.captureException(err);

      setOrderFlowState(OrderFlowState.SendingOrderFailed, undefined, err);
      throw err;
    }
  }

  export async function finalizeCloseOrder(
    closeOrderFunction: () => Promise<{ order: GcnOrder }>,
    checkoutSession: CheckoutSession,
    currentOrder: GcnOrder,
  ): Promise<void> {
    let sendKioskOrderErr: GcnError | undefined;
    let updatedOrder: GcnOrder | undefined;
    try {
      const closeOrderResponse = await closeOrderFunction();

      updatedOrder = closeOrderResponse.order;

      setOrderFlowState(OrderFlowState.OrderComplete);
      gcn.bridge.send({
        event: FlashBridgeMessage.ORDER_COMPLETED,
        guestId: checkoutSession.orderPayload2.order.guestId,
      });

      // Let the I9nManager handle errors itself
      I9nManager.sendOrder(closeOrderResponse.order).catch(() => {});
    } catch (err) {
      const { order, successfulTransactions } = await gcn.maitred.getOrderRequest(currentOrder.id);

      const orderWithTransactions = {
        ...order,
        transactions: successfulTransactions,
      };

      gcn.orderManager.setOrderFromJSON(orderWithTransactions);
      updatedOrder = gcn.orderManager.getOrder()!;

      if (!order.wasSentToPos) {
        // No need to setOrderFlowState set to error state in sendKioskOrder
        Sentry.captureMessage(err.message, {
          extra: {
            order: checkoutSession.orderPayload,
            error: err,
          },
        });
        sendKioskOrderErr = err;
      } else {
        setOrderFlowState(OrderFlowState.OrderComplete);
        gcn.bridge.send({
          event: FlashBridgeMessage.ORDER_COMPLETED,
          guestId: checkoutSession.orderPayload2.order.guestId,
        });
      }
    }

    if (GcnHardwareManager.hasReceiptPrinter()) {
      await printGuestReceipt(updatedOrder || currentOrder, sendKioskOrderErr);
    }
  }
}

export default OrderSender;
